import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { ShieldCheck, ExternalLink, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

/**
 * /transparency — public-facing one-table commitment page.
 * Pulls live state data from GET /api/public/jurisdictions (no auth).
 * Each row shows: State | Last Verified | Data Source (link).
 *
 * Distinct from /data-verification which is the deeper Sales-Tax + Privacy
 * methodology grid. This page is the headline trust narrative.
 */
export default function Transparency() {
  const [tax, setTax] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api
      .get("/public/jurisdictions")
      .then(({ data }) => setTax(data.tax || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? tax.filter(
          (r) =>
            r.state_name?.toLowerCase().includes(q) ||
            r.abbreviation?.toLowerCase().includes(q),
        )
      : tax;
    return [...filtered].sort((a, b) =>
      (a.state_name || "").localeCompare(b.state_name || ""),
    );
  }, [tax, query]);

  const mostRecent = useMemo(() => {
    if (!tax.length) return null;
    return tax
      .map((r) => r.last_verified_date)
      .filter(Boolean)
      .sort()
      .reverse()[0];
  }, [tax]);

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-6 py-12" data-testid="transparency-page">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">
          Data Integrity
        </div>
        <h1
          className="font-heading font-extrabold text-4xl sm:text-5xl text-[var(--rg-text-strong)] mb-4"
          data-testid="transparency-heading"
        >
          Our Data Transparency Commitment
        </h1>
        <p
          className="text-[var(--rg-text)] text-base max-w-3xl mb-10 leading-relaxed"
          data-testid="transparency-subheading"
        >
          Every jurisdiction in Regulens is verified against official government
          sources. Here&apos;s exactly when each was last checked.
        </p>

        {/* Headline KPI strip */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5" data-testid="transparency-kpi-states">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-muted)] mb-1">
              U.S. Jurisdictions Covered
            </div>
            <div className="font-heading font-extrabold text-3xl text-[var(--rg-text-strong)]">
              {tax.length || (loading ? "—" : 0)}
            </div>
          </div>
          <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5" data-testid="transparency-kpi-sources">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-muted)] mb-1">
              Verified Government Sources
            </div>
            <div className="font-heading font-extrabold text-3xl text-[var(--rg-text-strong)] flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-[var(--rg-sage)]" />
              100%
            </div>
          </div>
          <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5" data-testid="transparency-kpi-recent">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-muted)] mb-1">
              Most Recent Verification
            </div>
            <div className="font-heading font-extrabold text-3xl text-[var(--rg-text-strong)] font-mono">
              {mostRecent || (loading ? "—" : "n/a")}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--rg-muted)]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by state name or abbreviation…"
            className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] pl-10 max-w-md"
            data-testid="transparency-filter"
          />
        </div>

        {/* Table */}
        <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] overflow-x-auto" data-testid="transparency-table-wrap">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] border-b border-[var(--rg-border)]">
                <th className="py-3 px-4">State</th>
                <th className="py-3 px-4">Last Verified</th>
                <th className="py-3 px-4">Data Source</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={3} className="py-8 text-center text-[var(--rg-muted)]" data-testid="transparency-loading">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr
                    key={r.abbreviation}
                    className="border-b border-[var(--rg-border)]/60 bg-[var(--rg-bg)]/40 transition-fast"
                    data-testid={`transparency-row-${r.abbreviation}`}
                  >
                    <td className="py-3 px-4 text-[var(--rg-text-strong)] font-semibold">
                      {r.state_name}
                      <span className="text-[var(--rg-muted)] font-mono text-xs ml-2">({r.abbreviation})</span>
                    </td>
                    <td className="py-3 px-4 text-[var(--rg-text)] font-mono text-xs">
                      {r.last_verified_date || "—"}
                    </td>
                    <td className="py-3 px-4">
                      {r.official_url ? (
                        <a
                          href={r.official_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={r.official_url}
                          className="text-[var(--rg-sage)] hover:underline inline-flex items-center gap-1 text-xs font-semibold max-w-[260px] truncate"
                          data-testid={`transparency-source-${r.abbreviation}`}
                        >
                          <span className="truncate">Official Source</span>
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                      ) : (
                        <span className="text-[var(--rg-muted)] text-xs italic">Not published</span>
                      )}
                    </td>
                  </tr>
                ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={3} className="py-8 text-center text-[var(--rg-muted)]">
                    No states match your filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Trust narrative */}
        <div className="mt-10 rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage)]/5 p-6" data-testid="transparency-pledge">
          <div className="flex items-start gap-4">
            <ShieldCheck className="w-8 h-8 text-[var(--rg-sage)] flex-shrink-0" />
            <div>
              <h2 className="font-heading font-bold text-xl text-[var(--rg-text-strong)] mb-2">
                Why we publish this
              </h2>
              <p className="text-sm text-[var(--rg-text)] leading-relaxed">
                Compliance intelligence is only useful if you can trace it back
                to the source. Every figure in Regulens — from nexus thresholds
                to privacy-law enforcement contacts — points to a public
                government URL above. We re-verify these sources on a recurring
                schedule and surface the exact date here, with no marketing
                spin. If a source ever moves or changes, you&apos;ll see the next
                verification date update right on this page.
              </p>
              <div className="mt-4 flex flex-wrap gap-3 text-xs">
                <Link
                  to="/data-verification"
                  className="text-[var(--rg-sage)] hover:underline font-semibold"
                  data-testid="transparency-link-data-verification"
                >
                  See the full Privacy + Sales Tax methodology →
                </Link>
                <Link
                  to="/methodology"
                  className="text-[var(--rg-sage)] hover:underline font-semibold"
                  data-testid="transparency-link-methodology"
                >
                  How we score readiness →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
